// theme/assets/cart.js — slice-7
// Cart interactivity shared by the drawer + cart page.
// - Qty −/+ buttons → /cart/change.js (optimistic UI + revert on error)
// - Remove links → /cart/change.js {quantity: 0}
// - Note textarea → debounced /cart/update.js {note}  (Task 11 wires this)
// - Promo form → /cart/update.js {discount}  (Task 12 wires this)
// After every mutation: trigger cart-drawer-component.refresh() if mounted,
// or refresh the cart-page section directly.

// ---- Pure functions (testable) ----

export function parseLineIndex(attr) {
  const n = parseInt(attr, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function classifyChangeError(response) {
  if (!response || response.status === 0) return 'network';
  if (response.status === 422 || response.status === 404) return 'quantity';
  return 'network';
}

export function computeOptimisticSubtotal(items) {
  return items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
}

// ---- Browser-only side effects ----

if (typeof window !== 'undefined') {
  (function () {
    'use strict';
    const shopifyRoot = window.Shopify.routes.root;

    function getDrawer() {
      return document.querySelector('cart-drawer-component');
    }

    function getCartPage() {
      return document.querySelector('[data-cart-page]');
    }

    function refresh() {
      const drawer = getDrawer();
      const cartPage = getCartPage();
      if (drawer && typeof drawer.refresh === 'function') drawer.refresh();
      if (cartPage) refreshCartPage();
    }

    async function refreshCartPage() {
      try {
        const res = await fetch(`${shopifyRoot}cart?sections=cart`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.cart) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.cart, 'text/html');
        const newPage = doc.querySelector('[data-cart-page]');
        const currentPage = getCartPage();
        if (newPage && currentPage) {
          const scrollY = window.scrollY;
          currentPage.innerHTML = newPage.innerHTML;
          window.scrollTo(0, scrollY);
        }
      } catch (err) {
        console.warn('[cart] cart-page refresh failed', err);
      }
    }

    function flashUpdated(rowEl) {
      if (!rowEl) return;
      rowEl.classList.add('cart-item--just-updated');
      setTimeout(() => rowEl.classList.remove('cart-item--just-updated'), 1200);
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    }

    function showLineError(rowEl, message) {
      if (!rowEl) return;
      const stateEl = rowEl.querySelector('[data-cart-item-state]');
      if (!stateEl) return;
      stateEl.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.className = 'commerce-state commerce-state--error';
      wrap.setAttribute('role', 'status');
      wrap.setAttribute('aria-live', 'polite');
      const title = window.Theme?.translations?.cart_error_quantity_title || 'Erreur';
      const body = message || window.Theme?.translations?.cart_error_quantity_body || '';
      wrap.innerHTML = `
        <div class="commerce-state__content">
          <h3 class="commerce-state__title">${escapeHtml(title)}</h3>
          <p class="commerce-state__body">${escapeHtml(body)}</p>
        </div>`;
      stateEl.appendChild(wrap);
      setTimeout(() => { if (stateEl.contains(wrap)) wrap.remove(); }, 5000);
    }

    function getRowEl(target) {
      return target.closest('[data-cart-item]');
    }

    async function changeQuantity(lineIndex, newQty, rowEl) {
      // A Coming soon line may be removed, never increased or retained through
      // quantity controls. Shopify checkout enforcement is separate from this UI.
      if (newQty > 0 && rowEl?.dataset.comingSoon === 'true') return;
      const qtyOutput = rowEl ? rowEl.querySelector('.cart-item__qty-value') : null;
      const prevQty = qtyOutput ? parseInt(qtyOutput.textContent, 10) : null;

      // Optimistic flip
      if (qtyOutput && newQty > 0) qtyOutput.textContent = String(newQty);

      try {
        const res = await fetch(`${shopifyRoot}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: newQty }),
        });
        if (!res.ok) throw Object.assign(new Error('change-' + res.status), { response: res });
        const cart = await res.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = String(cart.item_count);
        });
        flashUpdated(rowEl);
        refresh();
        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: { item_count: cart.item_count, line: lineIndex },
          bubbles: true,
        }));
      } catch (err) {
        // Revert
        if (qtyOutput && prevQty != null) qtyOutput.textContent = String(prevQty);
        if (rowEl) rowEl.style.opacity = '';  // undo Retirer fade if applied
        const kind = classifyChangeError(err.response);
        const offlineMsg = window.Theme?.translations?.cart_error_offline;
        const qtyMsg = window.Theme?.translations?.cart_error_quantity_body;
        showLineError(rowEl, kind === 'network' ? offlineMsg : qtyMsg);
      }
    }

    function init() {
      // Cart drawer trigger — safety net. cart-drawer-component's own
      // connectedCallback also binds this; this document-level handler is
      // independent of the customElement lifecycle. Two fallback paths:
      // call showDialog() on the component if upgraded; native showModal()
      // on the inner <dialog> if not.
      document.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-cart-drawer-trigger]');
        if (!btn) return;
        event.preventDefault();
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.showDialog === 'function') {
          drawer.showDialog();
          return;
        }
        const innerDialog = drawer?.querySelector('dialog');
        if (innerDialog && !innerDialog.open && typeof innerDialog.showModal === 'function') {
          innerDialog.showModal();
        }
      });

      // Order note — debounced save
      const NOTE_DEBOUNCE_MS = 800;
      let noteSaveTimer = null;
      document.addEventListener('input', (event) => {
        const ta = event.target.closest('[data-cart-note-input]');
        if (!ta) return;
        clearTimeout(noteSaveTimer);
        noteSaveTimer = setTimeout(() => {
          const feedback = ta.parentElement.querySelector('[data-cart-note-feedback]');
          fetch(`${shopifyRoot}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: ta.value }),
          }).then((res) => {
            if (!res.ok) throw new Error('note-' + res.status);
            const savedMsg = window.Theme?.translations?.cart_note_saved;
            if (feedback && savedMsg) {
              feedback.textContent = savedMsg;
              setTimeout(() => { if (feedback) feedback.textContent = ''; }, 2000);
            }
          }).catch(() => {
            const offlineMsg = window.Theme?.translations?.cart_error_offline;
            if (feedback && offlineMsg) feedback.textContent = offlineMsg;
          });
        }, NOTE_DEBOUNCE_MS);
      });

      // Promo code apply — 3-state feedback (applied / pending-checkout / invalid)
      document.addEventListener('submit', async (event) => {
        const form = event.target.closest('[data-cart-promo-form]');
        if (!form) return;
        event.preventDefault();
        const input = form.querySelector('[data-cart-promo-input]');
        const feedback = form.parentElement.querySelector('[data-cart-promo-feedback]');
        if (!input || !feedback) return;
        const code = input.value.trim().toUpperCase();
        if (!code) return;

        feedback.innerHTML = '';
        try {
          const res = await fetch(`${shopifyRoot}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code }),
          });
          if (!res.ok) throw new Error('promo-' + res.status);
          const cart = await res.json();
          const applied = (cart.discount_applications || []).find((d) => (d.title || '').toUpperCase() === code);
          const tr = window.Theme?.translations || {};
          if (applied) {
            const msg = (tr.cart_promo_applied || '{{ code }} appliqué').replace('{{ code }}', code);
            feedback.innerHTML = renderState('success', msg);
          } else {
            feedback.innerHTML = renderState('success', tr.cart_promo_at_checkout || '');
          }
          refresh();
        } catch (err) {
          const tr = window.Theme?.translations || {};
          feedback.innerHTML = renderState('validation', tr.cart_promo_invalid || '');
        }

        function renderState(state, message) {
          return `
            <div class="commerce-state commerce-state--${state}" role="status" aria-live="polite">
              <div class="commerce-state__content">
                <p class="commerce-state__body">${escapeHtml(message)}</p>
              </div>
            </div>`;
        }
      });

      // Qty −/+
      document.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-cart-qty]');
        if (!btn) return;
        const rowEl = getRowEl(btn);
        if (!rowEl) return;
        const lineIndex = parseLineIndex(btn.dataset.cartLineIndex);
        if (lineIndex == null) return;
        const action = btn.dataset.cartQtyAction;
        const qtyOutput = rowEl.querySelector('.cart-item__qty-value');
        const currentQty = qtyOutput ? parseInt(qtyOutput.textContent, 10) : 1;
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        changeQuantity(lineIndex, newQty, rowEl);
      });

      // Remove
      document.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-cart-remove]');
        if (!btn) return;
        const rowEl = getRowEl(btn);
        if (!rowEl) return;
        const lineIndex = parseLineIndex(btn.dataset.cartLineIndex);
        if (lineIndex == null) return;
        rowEl.style.opacity = '0.4';
        changeQuantity(lineIndex, 0, rowEl);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
}
